import React, { useState } from 'react';
import { useCompanyEnrichment, TriggerEnrichmentPayload } from '@/hooks/useCompanyEnrichment';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { EnrichmentForm } from './components/EnrichmentForm';
import { ErrorDisplay } from './components/ErrorDisplay';
import { SuccessDisplay } from './components/SuccessDisplay';
import { useEnrichmentDebug } from './components/useEnrichmentDebug';

export function DirectEnrichmentTester() {
  const [companyName, setCompanyName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  
  const {
    showDebugInfo,
    setShowDebugInfo,
    debugInfo,
    clearDebugInfo,
    setErrorDebugInfo,
    setSuccessDebugInfo
  } = useEnrichmentDebug();
  
  const { triggerEnrichmentMutation: enrichCompany, isPending, error } = useCompanyEnrichment();

  const handleEnrichment = () => {
    clearDebugInfo();
    
    if (!companyName) {
      alert('Please enter a company name');
      return;
    }
    
    const originalConsoleError = console.error;
    const errorLogs: string[] = [];
    
    console.error = (...args) => {
      errorLogs.push(args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' '));
      originalConsoleError(...args);
    };
    
    try {
      const payload: TriggerEnrichmentPayload = {
        company_name: companyName,
        company_website: companyWebsite || undefined
      };
      enrichCompany(
        payload, 
        {
          onError: (err) => {
            console.error = originalConsoleError;
            setErrorDebugInfo(err, errorLogs, companyName, companyWebsite);
          },
          onSuccess: (data) => {
            console.error = originalConsoleError;
            setSuccessDebugInfo(data, companyName, companyWebsite);
          }
        }
      );
    } catch (e) {
      console.error = originalConsoleError;
      console.error('Unexpected error in enrichment handler:', e);
    }
  };

  return (
    <Card className="mt-8 mb-4">
      <CardHeader>
        <CardTitle>Company Enrichment Tester</CardTitle>
        <CardDescription>
          Test company data enrichment directly without creating a lead.
        </CardDescription>
      </CardHeader>
      
      <EnrichmentForm
        companyName={companyName}
        setCompanyName={setCompanyName}
        companyWebsite={companyWebsite}
        setCompanyWebsite={setCompanyWebsite}
        onEnrichment={handleEnrichment}
        isPending={isPending}
      />
      
      {error && (
        <ErrorDisplay
          error={error}
          debugInfo={debugInfo}
          showDebugInfo={showDebugInfo}
          setShowDebugInfo={setShowDebugInfo}
          companyName={companyName}
          companyWebsite={companyWebsite}
        />
      )}
      
      {debugInfo && debugInfo.success && (
        <SuccessDisplay
          debugInfo={debugInfo}
          showDebugInfo={showDebugInfo}
          setShowDebugInfo={setShowDebugInfo}
        />
      )}
    </Card>
  );
}
